import { v4 as uuidv4 } from 'uuid';
import createStore from './store';

enum PendingEditStatus {
  in_progress = 'in_progress',
  completed = 'completed',
  failed = 'failed'
}

export enum ClipSyncStatus {
  sync_completed = 'sync_completed',
  sync_in_progress = 'sync_in_progress',
  sync_failed = 'sync_failed'
}

export enum PendingEditType {
  clip = 'clip',
  chapter = 'chapter'
}

interface PendingEdit {
  edit_id: string;
  version: number;
  status: PendingEditStatus;
  type: PendingEditType;
}

type PendingEditStoreType = Record<string, PendingEdit[]>;

export const pendingEditsStore = createStore<PendingEditStoreType>({});

const removeRedundantEditsForType = (typedEdits: PendingEdit[]): PendingEdit[] => {
  let mostRecentSuccess = null as PendingEdit | null;
  let mostRecentFailed = null as PendingEdit | null;

  typedEdits.forEach(edit => {
    if (
      edit.status === PendingEditStatus.completed &&
      (mostRecentSuccess === null || edit.version > mostRecentSuccess.version)
    ) {
      mostRecentSuccess = edit as PendingEdit;
    }
    if (
      edit.status === PendingEditStatus.failed &&
      (mostRecentFailed === null || edit.version > mostRecentFailed.version)
    ) {
      mostRecentFailed = edit;
    }
  });

  const success = mostRecentSuccess;
  const failed = mostRecentFailed;

  if (success && failed) {
    // Keep the most recent success or failed edit
    const toKeep = failed.version > success.version ? failed : success;
    return typedEdits.filter(edit => edit.edit_id === toKeep.edit_id || edit.status === PendingEditStatus.in_progress);
  }

  const toKeep = success ?? failed;

  if (toKeep) {
    return typedEdits.filter(edit => edit.edit_id === toKeep.edit_id || edit.status === PendingEditStatus.in_progress);
  }

  return typedEdits;
};

const removeRedundantEdits = (pendingEdits: PendingEdit[]): PendingEdit[] => {
  const clipEdits = pendingEdits.filter(edit => edit.type === PendingEditType.clip);
  const chapterEdits = pendingEdits.filter(edit => edit.type === PendingEditType.chapter);
  const filteredEdits = [...removeRedundantEditsForType(clipEdits), ...removeRedundantEditsForType(chapterEdits)];
  const filteredEditIds = new Set(filteredEdits.map(edit => edit.edit_id));

  return pendingEdits.filter(edit => filteredEditIds.has(edit.edit_id));
};

const updateEditStatus = (clipId: string, edit_id: string, status: PendingEditStatus) => {
  pendingEditsStore.update((prev: PendingEditStoreType): PendingEditStoreType => {
    const edits = prev[clipId] ?? [];
    const updatedEditsForCurrentClip = edits.map(edit => {
      if (edit.edit_id === edit_id) {
        return { ...edit, status };
      }
      return edit;
    });
    const cleanedEdits = removeRedundantEdits(updatedEditsForCurrentClip);
    return { ...prev, [clipId]: cleanedEdits };
  });
};

export const addPendingUpdate = ({
  clipId,
  updatePromise,
  version,
  editType = PendingEditType.clip
}: {
  clipId: string;
  updatePromise: Promise<any>;
  version: number;
  editType?: PendingEditType;
}) => {
  const edit_id = uuidv4();

  // Push in_progress edit to the store
  pendingEditsStore.update((prev: PendingEditStoreType): PendingEditStoreType => {
    const existing = prev[clipId] ?? [];
    const pendingEdit: PendingEdit = {
      edit_id,
      version,
      status: PendingEditStatus.in_progress,
      type: editType
    };
    return { ...prev, [clipId]: [...existing, pendingEdit] };
  });

  updatePromise.then(
    () => updateEditStatus(clipId, edit_id, PendingEditStatus.completed),
    () => updateEditStatus(clipId, edit_id, PendingEditStatus.failed)
  );
};

export const isAnyClipUpdating = (pendingEdits: ReturnType<typeof pendingEditsStore.getSnapshot>) => {
  return Object.values(pendingEdits).some(edits => edits.some(edit => edit.status === PendingEditStatus.in_progress));
};

export const getClipUpdateStatus = (clipId: string, pendingEdits: ReturnType<typeof pendingEditsStore.getSnapshot>) => {
  const edits = pendingEdits[clipId] ?? [];
  if (edits.some(edit => edit.status === PendingEditStatus.in_progress)) {
    return ClipSyncStatus.sync_in_progress;
  }
  if (edits.some(edits => edits.status === PendingEditStatus.failed)) {
    return ClipSyncStatus.sync_failed;
  }
  return ClipSyncStatus.sync_completed;
};
